import { BybButtonVR, BybDialog } from "@Components/game/common";
import CenteredLoadingOverlay from "@Components/generic/centered-loading-overlay";
import { InviteResponse } from "@Shared/schemas/accounts/inviteSchemas";
import useI18n from "@i18n";
import { DialogContent, DialogProps, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { capitalize } from "lodash-es";
import { useState } from "react";

interface IEditPendingInviteDialog extends Omit<DialogProps, "onCancel"> {
  invite: InviteResponse;
  onDelete: () => Promise<any>;
  onResend: () => Promise<any>;
  onCancel: () => void;
}

const EditPendingInviteDialog = ({ invite, onDelete, onResend, onCancel, ...props }: IEditPendingInviteDialog) => {
  const { i18n } = useI18n();
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const [isLoading, setIsLoading] = useState(false);

  // Determine if this invite is for an existing user or just an email invite
  const isExistingUserInvite = Boolean(invite.recipient_user_id);

  const handleDelete = async () => {
    setIsLoading(true);
    try {
      await onDelete();
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setIsLoading(true);
    try {
      await onResend();
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    onCancel();
  };

  return (
    <BybDialog name="edit-pending-invite-dialog" maxWidth="lg" onClose={handleCancel} {...props}>
      <DialogContent>
        <CenteredLoadingOverlay loading={isLoading} />
        <Stack spacing={5}>
          <Stack spacing={2} sx={{ textAlign: "center" }}>
            <Typography variant={isSmDown ? "text1xl700" : "text4xl800"} color={theme.palette.text.primary}>
              {i18n("settings.members.invite.edit.title")}
            </Typography>
            <Typography variant={isSmDown ? "textBase500" : "text1xl500"} color={theme.palette.text.secondary}>
              {isExistingUserInvite
                ? i18n("settings.members.invite.edit.description.existing", {
                    email: invite.email,
                    userId: invite.recipient_user_id,
                  })
                : i18n("settings.members.invite.edit.description", { email: invite.email })}
            </Typography>
            {invite.recipient && (
              <Typography variant={isSmDown ? "textBase500" : "text1xl500"} color={theme.palette.text.secondary}>
                {i18n("settings.members.invite.edit.recipient", {
                  name: `${capitalize(invite.recipient.first_name)} ${capitalize(invite.recipient.last_name)}`,
                })}
              </Typography>
            )}
          </Stack>

          <Stack spacing={2} sx={{ width: "100%" }}>
            <Stack direction={"row"} spacing={2} sx={{ width: "100%" }}>
              <BybButtonVR
                color="primary"
                onClick={handleResend}
                fullWidth
                sx={{
                  backgroundColor: "dsSuccess.v300",
                  "&:hover": {
                    backgroundColor: "dsSuccess.v400",
                  },
                }}
              >
                {i18n("settings.members.invite.resend")}
              </BybButtonVR>

              <BybButtonVR
                onClick={handleDelete}
                fullWidth
                sx={{
                  backgroundColor: "dsDestructive.v300",
                  "&:hover": {
                    backgroundColor: "dsDestructive.v400",
                  },
                }}
              >
                {i18n("settings.members.invite.delete")}
              </BybButtonVR>
            </Stack>
            <BybButtonVR color="secondary" onClick={handleCancel} fullWidth>
              {i18n("button.cancel")}
            </BybButtonVR>
          </Stack>
        </Stack>
      </DialogContent>
    </BybDialog>
  );
};

export default EditPendingInviteDialog; 